import { IconButton, IconButtonProps } from "@mui/material";
import { styled } from "@mui/material/styles";
import { ICON_COLORS } from "common/icons/iconColors";

/**
 * Standardized IconButton component with consistent colors and hover/active states
 * Matches drawer navigation style: light grey when not hovered, black when hovered/selected
 *
 * Usage:
 * <StandardizedIconButton onClick={handleClick}>
 *   <SomeIcon />
 * </StandardizedIconButton>
 */
const StyledIconButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== "iconColor" && prop !== "selected",
})<{
  iconColor?:
    | "primary"
    | "secondary"
    | "text"
    | "error"
    | "inherit"
    | "default";
  selected?: boolean;
}>(({ theme, iconColor = "default", selected = false }) => {
  const getColor = () => {
    switch (iconColor) {
      case "primary":
        return ICON_COLORS.primary;
      case "secondary":
        return ICON_COLORS.secondary;
      case "error":
        return ICON_COLORS.error;
      case "inherit":
        return "inherit";
      case "text":
        return ICON_COLORS.text;
      default:
        return ICON_COLORS.default; // Light grey by default
    }
  };

  const baseColor = getColor();
  // If selected, use black; otherwise use base color
  const color =
    selected && iconColor === "default" ? ICON_COLORS.selected : baseColor;

  return {
    color: color,
    transition: "all 0.2s ease-in-out",

    "&:hover": {
      backgroundColor:
        iconColor === "primary"
          ? "rgba(28, 67, 33, 0.08)" // primary with opacity
          : "rgba(0, 0, 0, 0.04)", // default hover
      color:
        iconColor === "primary"
          ? ICON_COLORS.hover
          : iconColor === "default"
          ? ICON_COLORS.hover // Black on hover for default
          : color,
    },

    "&:active": {
      backgroundColor:
        iconColor === "primary"
          ? "rgba(28, 67, 33, 0.16)" // primary with more opacity
          : "rgba(0, 0, 0, 0.08)", // default active
      color:
        iconColor === "primary"
          ? ICON_COLORS.active
          : iconColor === "default"
          ? ICON_COLORS.active // Black on active for default
          : color,
    },

    "&.Mui-selected": {
      color: iconColor === "default" ? ICON_COLORS.selected : baseColor,
    },

    "&:disabled": {
      color: ICON_COLORS.disabled,
      backgroundColor: "transparent",
    },
  };
});

interface StandardizedIconButtonProps extends Omit<IconButtonProps, "color"> {
  /**
   * Icon color variant
   * - default: Light grey (#71717a) by default, black (#000000) on hover/selected (matches drawer)
   * - primary: Dark green (#1C4321) for primary actions
   * - secondary: Gray (#9E9E9E) for secondary/non-interactive
   * - text: Default text color for general use
   * - error: Red for error/warning states
   * - inherit: Inherit color from parent
   */
  iconColor?:
    | "primary"
    | "secondary"
    | "text"
    | "error"
    | "inherit"
    | "default";

  /**
   * Whether the button is in selected state (affects color for default variant)
   */
  selected?: boolean;
}

/**
 * Standardized IconButton with consistent colors and hover/active states
 * Matches drawer navigation style: light grey when not hovered, black when hovered/selected
 * Use this component instead of MUI's IconButton for consistent styling
 */
export default function StandardizedIconButton({
  iconColor = "default",
  selected = false,
  children,
  ...props
}: StandardizedIconButtonProps) {
  return (
    <StyledIconButton iconColor={iconColor} selected={selected} {...props}>
      {children}
    </StyledIconButton>
  );
}
